import React, { useEffect, useMemo, useRef, useState } from 'react'
import icBack from '@/assets/login2/ic-back.png'
import { sendVerifyCode, wxOauthBindMetis } from '@yuanfudao/metis-unified-login/es/service/login'
import { encryptStr, saveTokenToStorage, VERIFIER_CODE, VERIFIER_CODE_ZH } from '@yuanfudao/metis-unified-login'
import icLoading from '@/assets/login/loading.png'
import { formatCode, formatPhone, onNumberInputChange, phoneReg } from '@yuanfudao/metis-unified-login/es/utils/phone'
import { useGlobalState } from '@/store'
import bindSuccessIcon from '@/assets/login2/bind-success.png'
import bindFailIcon from '@/assets/login2/bind-fail.png'
import lessModule from './index.module.less'

export default function WxScanBind({
  onBack,
  afterBindSuccess,
  wxToken,
  csrfToken,
}: {
  onBack: () => void,
  afterBindSuccess: ()=>void,
  wxToken: string,
  csrfToken: string,
}) {
  const { updateGlobalState, setIsLogin } = useGlobalState()
  const [phone, setPhone] = useState('')
  const [identifyCode, setIdentifyCode] = useState('')
  const [codeText, setCodeText] = useState('获取验证码')
  const [sendDisabled, setSendDisabled] = useState(false)
  const timer = useRef(0)
  const [phoneErrText, setPhoneErrText] = useState('')
  const [codeErrText, setCodeErrText] = useState('')
  const [bindActive, setBindActive] = useState(false)
  const [bindStatus, setBindStatus] = useState<number>(0) // 0: 未填写绑定信息，1: 绑定成功，2: 绑定失败
  const redirectTimeout = useRef(0)

  const isPhoneValid = useMemo(() => phoneReg.test(phone), [phone])
  const isCodeValid = useMemo(() => identifyCode.length === 6, [identifyCode.length])

  const resetCode = () => {
    clearInterval(timer.current)
    setSendDisabled(false)
    setCodeText('重新获取验证码')
  }

  const handleSendCode = async () => {
    if (sendDisabled || !isPhoneValid) {
      return
    }
    try {
      const encrypPhone = encryptStr(phone) as string
      await sendVerifyCode({ phone: encrypPhone })
      setSendDisabled(true)
      let seconds = 60
      setCodeText(`${seconds} 秒后重试`)
      timer.current = window.setInterval(() => {
        seconds--
        setCodeText(`${seconds} 秒后重试`)
        if (seconds <= 0) {
          resetCode()
        }
      }, 1000)
      setCodeErrText('')
    } catch (e: any) {
      resetCode()
      console.warn(e?.response)
      let text = ''
      if (e?.response?.status) {
        text = VERIFIER_CODE_ZH[e?.response?.status as VERIFIER_CODE]
      } else {
        text = '获取验证码失败，请稍后重试'
      }
      setCodeErrText(text)
    }
  }

  const onPhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const p = e.target.value.replace(/[^0-9]/g, '')
    if (p.length > 0 && !phoneReg.test(p)) {
      setPhoneErrText('手机号格式错误')
    } else {
      setPhoneErrText('')
    }
  }

  const handleLogin = async () => {
    if ((isCodeValid) && (isPhoneValid) && !bindActive) {
      try {
        setPhoneErrText('')
        setCodeErrText('')
        setBindActive(true)
        const encrypPhone = encryptStr(phone) as string
        const encrypCode = encryptStr(identifyCode) as string
        const { data: { code, result, message } } = await wxOauthBindMetis({
          phone: encrypPhone,
          verification: encrypCode,
          wxToken,
          userAgent: 'web',
          csrfToken,
        })
        setBindActive(false)
        if (code === '0' && result && result.token?.accessToken && result.token?.refreshToken) {
          saveTokenToStorage(result.token.accessToken, result.token.refreshToken)
          updateGlobalState()
          setIsLogin(true)
          setCodeErrText('绑定成功，页面即将跳转…')
          setBindStatus(1)
          redirectTimeout.current = window.setTimeout(() => {
            console.log('跳转')
            // redirectByUrl()
            afterBindSuccess()
          }, 1000)
        } else {
          onFail(message)
        }
      } catch (e: any) {
        console.warn(e)
        setBindActive(false)
        onFail(e?.response?.data?.message)
      }
    }
  }

  const onFail = (msg?: string) => {
    setBindStatus(2)
    if (msg) {
      setCodeErrText(msg)
    } else {
      const text = '绑定失败'
      setCodeErrText(text)
    }
  }

  useEffect(() => () => {
    clearTimeout(redirectTimeout.current)
  }, [])

  return (
    <div className={lessModule.WxScanBindWrap}>
      <div className={lessModule.header}>
        <div className={lessModule.backWrap} onClick={onBack}>
          <img className={lessModule.icBack} src={icBack} alt="" />
        </div>
        <div className={`${lessModule.title} fontBold`}>绑定账号</div>
      </div>

      <>
        {
          !bindStatus ? (
            <div className={lessModule.content}>
              <div
                className={`${lessModule.inputWrap}`}
              >
                <input
                  className={lessModule.phone}
                  type="text"
                  autoComplete="tel"
                  name="tel"
                  placeholder="请输入手机号"
                  value={formatPhone(phone)}
                  onChange={(e) => onNumberInputChange(e, 11, setPhone)}
                  onBlur={onPhoneBlur}
                />
                <div className={lessModule.textError}>{phoneErrText}</div>
              </div>
              <div
                className={lessModule.inputWrap}
              >
                <input
                  className={lessModule.code}
                  type="text"
                  placeholder="请输入验证码"
                  value={formatCode(identifyCode)}
                  onChange={(e) => {
                    onNumberInputChange(e, 6, setIdentifyCode)
                  }}
                />
                <span
                  style={sendDisabled ? { cursor: 'not-allowed', minWidth: '80px' } : {}}
                  className={`${lessModule.sendCode} ${(isPhoneValid && !sendDisabled) ? lessModule.active : ''}`}
                  onClick={handleSendCode}
                >{codeText}</span>
                <div className={lessModule.textError}>{codeErrText}</div>
              </div>
              <div className={lessModule.inputWrap}>
                <div
                  className={`${lessModule.loginBtn} fontBold ${(isCodeValid) && (isPhoneValid) ? lessModule.active : ''}`}
                  style={bindActive ? { cursor: 'not-allowed' } : {}}
                  onClick={handleLogin}
                >
                  {
                    bindActive ? (
                      <>
                        <img className={lessModule.loading} src={icLoading} alt="" />
                        绑定中...
                      </>
                    ) : '确定'
                  }
                </div>
              </div>
            </div>
          ) : (
            <div className={lessModule.bindRes}>
              <img className={lessModule.resIcon} src={bindStatus === 1 ? bindSuccessIcon : bindFailIcon} alt="" />
              <div className={lessModule.resText}>
                {
                codeErrText
                }
              </div>
            </div>
          )
        }
      </>
    </div>
  )
}
